import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkle, X } from "@phosphor-icons/react";
import {
  buildOpportunityReasoning,
  TONE_STYLES,
  SOURCE_META,
  getFreshness,
  getCompetitionVerdict,
} from "../lib/reasoning";
import { CompetitionVerdict } from "./CompetitionVerdict";
import FreshnessBadge from "./FreshnessBadge";
import ShareOppButton from "./ShareOppButton";
import GuideHelpButton from "./GuideHelpButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical map of AI pain-bucket keys → human label + accent color.
// Shared across the AI Pulse header bar chart and per-card bucket chips.
export const AI_PAIN_BUCKETS_MAP = {
  cost: { label: "Cost & Pricing", color: "#F97316" },
  hallucination: { label: "Hallucination", color: "#EF4444" },
  context: { label: "Context & Memory", color: "#F59E0B" },
  integration: { label: "Integration", color: "#8B5CF6" },
  quality: { label: "Output Quality", color: "#06B6D4" },
  speed: { label: "Speed", color: "#10B981" },
  privacy: { label: "Privacy", color: "#6366F1" },
  voice: { label: "Voice & Audio", color: "#EC4899" },
  image: { label: "Image Gen", color: "#A855F7" },
  video: { label: "Video Gen", color: "#3B82F6" },
  agent_reliability: { label: "Agent Reliability", color: "#F43F5E" },
  domain_niche: { label: "Niche-Specific", color: "#14B8A6" },
};

// Per-source label + brand-accent color displayed in the header chips row.
const AI_PULSE_SOURCE_META = [
  { k: "reddit",          label: "Reddit",        color: "#FF4500" },
  { k: "hackernews",      label: "Hacker News",   color: "#FF6600" },
  { k: "fiverr",          label: "Fiverr",        color: "#1DBF73" },
  { k: "freelancer",      label: "Freelancer",    color: "#29B2FE" },
  { k: "upwork",          label: "Upwork",        color: "#14A800" },
  { k: "indiehackers",    label: "IndieHackers",  color: "#0E2439" },
  { k: "producthunt",     label: "ProductHunt",   color: "#DA552F" },
  { k: "github_trending", label: "GitHub",        color: "#24292E" },
  { k: "youtube",         label: "YouTube",       color: "#FF0000" },
  { k: "g2",              label: "G2 reviews",    color: "#FF492C" },
];

const AIPulseDashCard = ({ opp, onSelectOpp }) => {
  const score = Math.round(opp.weighted_total || 0);
  const verdict = opp.verdict || "PENDING";
  const verdictBg = score >= 85 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : score >= 50 ? "bg-zinc-400" : "bg-zinc-300";
  const sb = opp.source_breakdown || {};
  const sources = Object.entries(SOURCE_META)
    .map(([k, m]) => ({ k, ...m, v: sb[k] || 0 }))
    .filter(s => s.v > 0);
  const bucket = AI_PAIN_BUCKETS_MAP[opp.pain_bucket];
  const reasoning = buildOpportunityReasoning(opp);
  const tone = TONE_STYLES[reasoning.tone];
  const comp = getCompetitionVerdict(opp);

  return (
    <button
      onClick={() => onSelectOpp?.(opp)}
      className="text-left bg-white border border-zinc-100 rounded-2xl p-5 hover:border-violet-300 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
      data-testid={`ai-pulse-dash-card-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] uppercase tracking-[0.16em] font-semibold">
            <Sparkle size={9} weight="fill" /> {opp.ai_category || "AI"}
          </div>
          <FreshnessBadge opp={opp} />
        </div>
        <div className="flex items-center gap-1.5">
          <ShareOppButton oppId={opp.id} testId={`ai-pulse-share-${opp.id}`} />
          <div className={`inline-flex items-center px-2 py-0.5 rounded-full ${verdictBg} text-white text-[10px] uppercase tracking-[0.16em] font-bold`}>{verdict}</div>
        </div>
      </div>

      <p className="text-sm font-semibold text-zinc-900 leading-snug line-clamp-3 mb-3 min-h-[60px]">{opp.problem}</p>

      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-3xl font-bold text-zinc-900 tabular-nums">{score}</span>
        <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">P2P Score</span>
        {bucket && (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-100 text-[10px] font-semibold text-zinc-700">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: bucket.color }} />
            {bucket.label}
          </span>
        )}
      </div>

      {reasoning.lines.length > 0 && (
        <div className="mb-3 pt-3 border-t border-zinc-100">
          <div className="text-[10px] uppercase tracking-[0.16em] text-violet-700 font-bold mb-2">Why this matters</div>
          <ul className="space-y-1.5">
            {reasoning.lines.map((line, i) => (
              <li key={i} className="text-[11px] text-zinc-700 leading-relaxed flex gap-1.5">
                <span className="flex-shrink-0">{line.icon}</span>
                <span>{line.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {comp ? (
        <div className="mb-3">
          <CompetitionVerdict opportunity={opp} compact heading="Competition check" />
        </div>
      ) : (
        <div className={`mb-3 px-2.5 py-2 rounded-lg border ${tone.bg} ${tone.border} ${tone.text}`}>
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70 mb-0.5">Recommended action · {tone.label}</div>
          <div className="text-[11px] font-semibold leading-snug">{reasoning.action}</div>
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-1.5 pt-3 border-t border-zinc-100">
        <div className="w-full text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1">Evidence sources</div>
        {sources.length === 0 && <span className="text-[10px] text-zinc-400">No source evidence yet</span>}
        {sources.map((s) => (
          <span key={s.k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-50 border border-zinc-100 text-[10px] font-semibold text-zinc-700">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
            {s.label} <span className="tabular-nums">{s.v}</span>
          </span>
        ))}
      </div>
    </button>
  );
};

const AIPulseView = ({ onSelectOpp }) => {
  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucketFilter, setBucketFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [freshFilter, setFreshFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [oRes, sRes, aRes] = await Promise.all([
          axios.get(`${API}/ai/pulse?limit=100`, { timeout: 12000 }),
          axios.get(`${API}/ai/pulse/stats`, { timeout: 12000 }),
          axios.get(`${API}/competition/audit-stats`, { timeout: 12000 }).catch(() => null),
        ]);
        if (cancelled) return;
        setOpps(Array.isArray(oRes.data) ? oRes.data : []);
        setStats(sRes.data);
        if (aRes && aRes.data) setAudit(aRes.data);
      } catch (e) {
        if (!cancelled) { setOpps([]); }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = opps.filter(o => {
    if (bucketFilter && o.pain_bucket !== bucketFilter) return false;
    if (sourceFilter) {
      const sb = o.source_breakdown || {};
      if ((sb[sourceFilter] || 0) <= 0) return false;
    }
    if (freshFilter === "new_today" && getFreshness(o) !== "new_today") return false;
    if (freshFilter === "fresh_week" && getFreshness(o) === null) return false;
    return true;
  });
  const counts = {
    new_today:  opps.filter(o => getFreshness(o) === "new_today").length,
    fresh_week: opps.filter(o => getFreshness(o) !== null).length,
  };

  const buckets = stats?.pain_bucket_distribution || [];
  const maxBucket = buckets[0]?.count || 1;
  const totalSignals = stats?.ai_signals_by_source?.total || 0;
  const bySource = stats?.ai_signals_by_source || {};

  return (
    <div className="space-y-6" data-testid="ai-pulse-view">
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-[0.18em]">
            <Sparkle size={12} weight="fill" /> AI Pulse · live
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 flex items-center gap-2 flex-wrap">
            <span><span className="tabular-nums">{stats?.total_ai_opportunities ?? "—"}</span> AI opportunities · <span className="tabular-nums">{totalSignals.toLocaleString()}</span> signals</span>
            <GuideHelpButton feature="ai_pulse" />
          </h1>
          <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
            Every signal below is a real post, gig, launch or repo our scanners pulled in the last 30 days. The number after each source = how many AI-tagged documents we've collected from it.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {AI_PULSE_SOURCE_META.map((s) => (
              <span
                key={s.k}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-zinc-200 text-[11px] font-semibold text-zinc-700"
                data-testid={`source-stat-${s.k}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                {s.label} <span className="tabular-nums text-zinc-500">{bySource[s.k] ?? 0}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="w-full lg:w-[420px] bg-white border border-zinc-100 rounded-2xl p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-3">Top AI pain right now</div>
          <div className="space-y-1.5">
            {buckets.slice(0, 6).map((b) => {
              const meta = AI_PAIN_BUCKETS_MAP[b.bucket] || { label: b.bucket, color: "#8B5CF6" };
              const isActive = bucketFilter === b.bucket;
              return (
                <button
                  key={b.bucket}
                  onClick={() => setBucketFilter(isActive ? "" : b.bucket)}
                  className={`w-full flex items-center gap-2 px-1 py-1 rounded transition-colors hover:bg-zinc-50 ${isActive ? "bg-violet-50" : ""}`}
                  data-testid={`bucket-filter-${b.bucket}`}
                >
                  <div className="text-[11px] text-zinc-900 font-medium w-[120px] text-left truncate">{meta.label}</div>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(b.count / maxBucket) * 100}%`, background: meta.color }} />
                  </div>
                  <div className="text-[11px] tabular-nums text-zinc-500 w-7 text-right">{b.count}</div>
                </button>
              );
            })}
            {buckets.length === 0 && <div className="text-xs text-zinc-400">Loading signals…</div>}
          </div>
        </div>
      </div>

      {audit && audit.cumulative && audit.cumulative.total > 0 && (
        <div
          className="rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50 p-4"
          data-testid="competition-audit-banner"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold mb-1">
                Competition self-audit
              </div>
              <div className="text-sm font-semibold text-zinc-900 leading-snug" data-testid="audit-cumulative">
                {audit.cumulative.downgraded} of {audit.cumulative.total} all-time “BUILD” opportunities were
                downgraded to YELLOW or RED after the competition check
                <span className="text-rose-600"> ({audit.cumulative.pct}%)</span>.
              </div>
              <div className="text-[11px] text-zinc-500 mt-1" data-testid="audit-secondary">
                Last 7 days: {audit.last_7d.downgraded}/{audit.last_7d.total} ({audit.last_7d.pct}%)
                {" · "}{audit.cumulative.red} crowded
                {audit.cumulative.unverified > 0 && ` · ${audit.cumulative.unverified} unverified`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                Check cost · this month
              </div>
              <div className="text-lg font-bold text-zinc-900 tabular-nums" data-testid="audit-monthly-cost">
                ${typeof audit.monthly_cost_usd === "number" ? audit.monthly_cost_usd.toFixed(2) : audit.monthly_cost_usd}
              </div>
              <div className="text-[10px] text-zinc-400">
                {audit.monthly_checks} checks · GitHub {audit.github_auth}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mr-1">Recency:</span>
        {[
          { k: "", l: `All ${opps.length}` },
          { k: "new_today",  l: `🆕 New today (${counts.new_today})` },
          { k: "fresh_week", l: `🔥 This week (${counts.fresh_week})` },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setFreshFilter(s.k)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${freshFilter === s.k ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-emerald-400"}`}
            data-testid={`fresh-filter-${s.k || "all"}`}
          >
            {s.l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mr-1">Source filter:</span>
        {[
          { k: "", l: "All sources" },
          { k: "reddit", l: "Reddit" },
          { k: "hackernews", l: "Hacker News" },
          { k: "fiverr", l: "Fiverr" },
          { k: "freelancer", l: "Freelancer" },
          { k: "upwork", l: "Upwork" },
          { k: "indiehackers", l: "IndieHackers" },
          { k: "producthunt", l: "ProductHunt" },
          { k: "github_trending", l: "GitHub" },
          { k: "youtube", l: "YouTube" },
          { k: "g2", l: "G2 reviews" },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setSourceFilter(s.k)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${sourceFilter === s.k ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-violet-300"}`}
            data-testid={`source-filter-${s.k || "all"}`}
          >
            {s.l}
          </button>
        ))}
        {(bucketFilter || sourceFilter || freshFilter) && (
          <button
            onClick={() => { setBucketFilter(""); setSourceFilter(""); setFreshFilter(""); }}
            className="px-3 py-1 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            data-testid="ai-pulse-clear-filters"
          >
            <X size={11} weight="bold" className="inline mr-1" /> Clear
          </button>
        )}
        <span className="ml-auto text-xs text-zinc-500 tabular-nums">{filtered.length} opportunities shown</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className="bg-white border border-zinc-100 rounded-2xl p-5 animate-pulse">
              <div className="h-3 w-24 bg-zinc-200 rounded mb-3" />
              <div className="h-5 w-3/4 bg-zinc-200 rounded mb-2" />
              <div className="h-5 w-1/2 bg-zinc-200 rounded" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-100 rounded-2xl p-12 text-center">
          <Sparkle size={32} weight="duotone" className="mx-auto text-zinc-400 mb-3" />
          <h3 className="text-base font-semibold text-zinc-900 mb-1">No AI opportunities match these filters yet</h3>
          <p className="text-sm text-zinc-500">Try clearing filters or wait for the next scan cycle.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((opp) => <AIPulseDashCard key={opp.id} opp={opp} onSelectOpp={onSelectOpp} />)}
        </div>
      )}
    </div>
  );
};

export default AIPulseView;
